import { doc, deleteDoc, writeBatch, query, where, getDocs, collection } from "firebase/firestore";
import { db } from "@/config/firebase";

// Collection names
export const collections = {
  users: "users",
  events: "events",
  kiosk_sessions: "kiosk_sessions",
  game_stats: "game_stats",
  bets: "bets",
  event_signups: "event_signups",
};

/**
 * Delete a single event by ID
 * @param {string} eventId - Firestore document ID
 * @returns {Promise<{ok: boolean, message: string}>}
 */
export async function deleteEventById(eventId) {
  if (!eventId) {
    return { ok: false, message: "Keine Event-ID angegeben" };
  }

  try {
    console.group(`🗑️ Deleting event ${eventId}`);
    
    // 1. Delete event document
    await deleteDoc(doc(db, collections.events, eventId));
    console.log("✅ Event document deleted");
    
    // 2. Delete related signups
    const signupsQuery = query(
      collection(db, collections.event_signups),
      where("event_id", "==", eventId)
    );
    const signupsSnap = await getDocs(signupsQuery);
    
    if (signupsSnap.size > 0) {
      const batch = writeBatch(db);
      signupsSnap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
      console.log(`✅ Deleted ${signupsSnap.size} signups`);
    }
    
    console.groupEnd();
    return { 
      ok: true, 
      message: `Event und ${signupsSnap.size} Anmeldungen gelöscht` 
    };
    
  } catch (error) {
    console.error("❌ Error deleting event:", error);
    console.groupEnd();
    return { 
      ok: false, 
      message: `Fehler beim Löschen: ${error.message}` 
    };
  }
}

/**
 * Delete multiple events by type using batch operations
 * @param {string} eventType - Event type to delete (e.g., "training")
 * @returns {Promise<{ok: boolean, deleted: number, message: string}>}
 */
export async function deleteEventsByType(eventType) {
  if (!eventType) {
    return { ok: false, deleted: 0, message: "Kein Event-Typ angegeben" };
  }

  try {
    console.group(`🗑️ Bulk delete events of type: ${eventType}`);
    
    // 1. Load all events of this type
    const eventsQuery = query(
      collection(db, collections.events),
      where("type", "==", eventType)
    );
    const eventsSnap = await getDocs(eventsQuery);
    
    if (eventsSnap.size === 0) {
      console.log("ℹ️ No events found");
      console.groupEnd();
      return { ok: true, deleted: 0, message: "Keine Events gefunden" };
    }
    
    console.log(`📋 Found ${eventsSnap.size} events to delete`);
    
    // 2. Delete events in batches (Firestore limit: 500 per batch)
    const BATCH_SIZE = 500;
    let deleted = 0;
    
    for (let i = 0; i < eventsSnap.docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchDocs = eventsSnap.docs.slice(i, i + BATCH_SIZE);
      
      batchDocs.forEach(d => {
        batch.delete(d.ref);
      });
      
      await batch.commit();
      deleted += batchDocs.length;
      console.log(`✅ Deleted batch: ${deleted}/${eventsSnap.size}`);
    }
    
    // 3. Delete related signups
    const eventIds = eventsSnap.docs.map(d => d.id);
    let signupsDeleted = 0;
    
    // Query signups in chunks (Firestore 'in' limit: 10)
    for (let i = 0; i < eventIds.length; i += 10) {
      const chunk = eventIds.slice(i, i + 10);
      const signupsQuery = query(
        collection(db, collections.event_signups),
        where("event_id", "in", chunk)
      );
      const signupsSnap = await getDocs(signupsQuery);
      
      if (signupsSnap.size > 0) {
        const batch = writeBatch(db);
        signupsSnap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
        signupsDeleted += signupsSnap.size;
      }
    }
    
    console.log(`✅ Deleted ${signupsDeleted} signups`);
    console.groupEnd();
    
    return { 
      ok: true, 
      deleted, 
      message: `${deleted} Events und ${signupsDeleted} Anmeldungen gelöscht` 
    };
    
  } catch (error) {
    console.error("❌ Error in bulk delete:", error);
    console.groupEnd();
    return { 
      ok: false, 
      deleted: 0, 
      message: `Fehler: ${error.message}` 
    };
  }
}
